import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import style from './index.scss';

/**
 * FIN-14030 — the shell's one copy affordance, moved verbatim out of
 * FpAlertHeader (where it copies the Txn ID) so the Event Detail panel's Event
 * ID can offer the same control. One copy idiom per page: a selectable tooltip
 * on the panel would have been a second. The only addition is `ariaLabel`, so
 * each consumer names what it copies; the header keeps "Copy transaction ID".
 */

/**
 * lucide `copy`, at the design's measured 9 x 9 with stroke-width 2. Hand-written
 * rather than imported for the same reason as ExplanationPanel's ChevronDown:
 * lucide is not a dependency here, and antd has no equivalent glyph — its
 * CopyOutlined is a filled icon-font shape, visibly heavier beside 11px text.
 * Paths copied verbatim from the design's DOM.
 */
export const CopyIcon = () => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v2" />
  </svg>
);

/**
 * Copies the FULL value — the text beside it is truncated, so this and the hover
 * title are how to get the whole value.
 *
 * The tooltip doubles as the confirmation ("Copy" -> "Copied"): a toast would be
 * heavy for a one-character interaction, and without any feedback there is no way
 * to tell whether the click registered.
 */
const CopyButton = ({ value, ariaLabel }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  // Cleared on unmount: the alert header remounts whenever the analyst opens a
  // different alert, and a pending timer would setState on a dead component.
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = () => {
    // Optional-chained deliberately. `navigator.clipboard` is undefined outside a
    // secure context (plain http on a non-localhost host, which is how the QA
    // environments are reached) and in jsdom. Failing silently is better than
    // throwing out of a click handler on a fraud screen.
    navigator.clipboard?.writeText(value);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <button
        type="button"
        className={style.copyButton}
        onClick={copy}
        aria-label={ariaLabel}>
        <CopyIcon />
      </button>
    </Tooltip>
  );
};

CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string
};

CopyButton.defaultProps = { ariaLabel: 'Copy' };

export default CopyButton;
